import { useCallback, useState } from 'react';
import { CognitoError } from '../../../providers/Auth/utils/types';
import { useAuthProvider } from '../../../providers/Auth';
import { useSnackbar } from '../../../providers/Snackbar';
import { CognitoUserAttribute } from 'amazon-cognito-identity-js';
import { getUserFriendlyError } from '../../../utils/errorMapper';
import { useConfig } from '../../../providers/Config';
import { captureException } from '@sentry/minimal';
import { setCacheBuster } from '../../../utils';

/**
 * Handles the login request
 */
export const useLogin = (): {
  handleLogin: (
    username: string,
    userpass: string,
    newPassword?: string,
    attributes?: CognitoUserAttribute[],
  ) => Promise<boolean>;
  loading: boolean;
} => {
  const [loading, setLoading] = useState<boolean>(false);
  const { signIn } = useAuthProvider();
  const { setSnackbar } = useSnackbar();
  const {
    tenantContact: { email },
  } = useConfig();

  const handleLogin = useCallback(
    async (username: string, userpass: string, newPassword?: string, attributes?: CognitoUserAttribute[]) => {
      return (async () => {
        try {
          setLoading(true);
          await signIn(username, userpass, newPassword, attributes);
          setCacheBuster();
          return true;
        } catch (error) {
          const cognitoError: CognitoError = error.error;
          captureException(cognitoError ? cognitoError : error);
          setSnackbar({
            open: true,
            severity: 'error',
            message:
              (cognitoError && getUserFriendlyError(cognitoError.code, cognitoError.message, { email })) ||
              getUserFriendlyError('Unexpected', undefined, { email }),
          });
        } finally {
          setLoading(false);
        }
        return false;
      })();
    },
    [],
  );

  return {
    handleLogin,
    loading,
  };
};
